import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { API_ENDPOINTS } from 'core/constant/API_ENDPOINTS';
import DataTableWrapper from 'components/common/DataTableWrapper';
import EditProjectFloorPlanModal from 'components/admin/common/modal/projects/EditProjectFloorPlanModal';
import ConfirmDeleteModal from 'components/common/modal/ConfirmDeleteModal';
import useModalState from 'core/hooks/state/useModalState';
import { validateForm } from 'core/utils/validation';
import useCrud from 'core/hooks/api/useCrud';
import { Alert } from 'react-bootstrap';
import usePut from 'core/hooks/api/usePut';
import usePost from 'core/hooks/api/usePost';
import useDelete from 'core/hooks/api/useDelete';
import { typeDetail } from 'core/constant/SELECT_OPTIONS';

const ProjectsFloorPlan = () => {
    const {
        fetchData: data,
        fetchError: error,
        refetch,
        postData: createFloorPlanData,
        postError: creatingError,
        postLoading: creating,
        putData: updateFloorPlanData,
        putError: updatingError,
        putLoading: updating,
        deleteData: deleteFloorPlanData,
        deleteLoading: deleting,
        deleteError: deletingError,
        clearErrors: clearServerErrors
    } = useCrud(API_ENDPOINTS.FLOOR_PLANS);

    const { putData: updateFloorDetail, loading: uploadingFloorDetail } = usePut(API_ENDPOINTS.FLOOR_PLAN_DETAILS);
    const { postData: createFloorDetail, loading: creatingFloorDetail } = usePost(API_ENDPOINTS.FLOOR_PLAN_DETAILS);
    const { deleteData: deleteFloorDetail, loading: deletingFloorDetail } = useDelete(API_ENDPOINTS.FLOOR_PLAN_DETAILS);
    const { fetchData: projectData } = useCrud(API_ENDPOINTS.PROJECTS);
    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState(null);

    const handleCloseEditFloorPlan = () => {
        setErrors({});
        clearServerErrors();
    };
    const { isOpen: isOpenEditFloorPlan, modalData: selectedFloorPlan, openModal: openEditFloorPlan, closeModal: closedEditFloorPlan } = useModalState({
        onClose: handleCloseEditFloorPlan
    });

    const { isOpen: isOpenDeleteFloorPlan, modalData: FloorPlanToDelete, openModal: openDeleteFloorPlan, closeModal: closedDeleteFloorPlan } = useModalState({
        onClose: handleCloseEditFloorPlan
    });

    const memoizedFloorPlans = useMemo(() => selectedFloorPlan?.details || [], [selectedFloorPlan]);

    const validationRules = [
        { field: 'name', method: 'required' },
        { field: 'project_id', method: 'required' },
        { field: 'description', method: 'required' }
    ];

    const validationFeaturesRules = [
        { field: 'label', method: 'required' },
        { field: 'value', method: 'required' },
    ];

    const projectsOptions = useMemo(() => {
        if (projectData?.length > 0) {
            return projectData.map(project => ({
                label: project.title,
                value: project.id
            }));
        } else {
            return [{ label: 'Default', value: 1 }, { label: 'Default', value: 2 }];
        }
    }, [projectData]);

    const handleModalSave = useCallback(async (updatedFloorPlan, features, featuresToDelete) => {
        setSuccessMessage(null);
        clearServerErrors();

        const formErrors = validateForm(updatedFloorPlan, validationRules);
        const featuresErrors = features.map((item, index) => {
            const featureErrors = validateForm(item, validationFeaturesRules);
            return Object.keys(featureErrors).reduce((acc, key) => {
                if (!acc[index]) acc[index] = {};
                acc[index][key] = featureErrors[key];
                return acc;
            }, {});
        }).filter(error => Object.keys(error).length > 0);

        const combinedErrors = { ...formErrors, ...featuresErrors.reduce((acc, curr) => ({ ...acc, ...curr }), {}) };

        if (Object.keys(combinedErrors).length > 0) {
            setErrors(combinedErrors);
            return;
        }

        setErrors({});
        try {
            let message = '';
            let floorPlanId = updatedFloorPlan.id;
            if (floorPlanId) {
                await updateFloorPlanData(floorPlanId, updatedFloorPlan, true);
                message = "Plano de planta actualizado con éxito.";
            } else {
                const createdFloorPlan = await createFloorPlanData(updatedFloorPlan, true);
                floorPlanId = createdFloorPlan.id;
                message = "Plano de planta creado con éxito.";
            }

            for (const feature of features) {
                if (feature.label.trim() === '' && feature.value.trim() === '') {
                    continue;
                }
                feature.floor_plan_id = floorPlanId;
                if (feature.id) {
                    await updateFloorDetail(feature.id, feature, true);
                } else {
                    await createFloorDetail(feature, true);
                }
            }

            for (const featureId of featuresToDelete) {
                await deleteFloorDetail(featureId);
            }

            closedEditFloorPlan();
            setErrors({});
            setSuccessMessage(message);
            refetch();
        } catch (error) {
            console.error(error);
        }
    }, [updateFloorPlanData, createFloorPlanData, updateFloorDetail, createFloorDetail, deleteFloorDetail, refetch]);

    const handleCreateClick = useCallback(() => {
        setErrors({});
        const newFloorPlan = {
            name: '',
            description: ''
        };
        openEditFloorPlan(newFloorPlan);
    }, []);

    const handleEditClick = useCallback((floorPlan) => {
        openEditFloorPlan(floorPlan);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        setSuccessMessage(null);
        if (FloorPlanToDelete) {
            try {
                await deleteFloorPlanData(FloorPlanToDelete.id);
                setSuccessMessage("Detalle eliminado con éxito.");
                refetch();
                closedDeleteFloorPlan();
            } catch (error) {
                console.error(error);
            }
        }
    }, [FloorPlanToDelete, deleteFloorPlanData, refetch]);

    const columns = useMemo(() => [
        {
            field: 'name',
            header: 'Tipo de Detalle',
            sortable: true,
            body: (rowData) => {
                const detailType = typeDetail.find(detail => detail.value === rowData.name);
                return detailType ? detailType.label : 'Tipo desconocido';
            }
        },
        { field: 'description', header: 'Descripción', sortable: true }
    ], []);

    if (error) return <p>Error: {error.message}</p>;

    return (
        <div>
            <div className="row w-100 mb-2">
                <div className="p-0 sub-title Service mb-2">
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <h5 className="m-0 text-title">Detalle de Proyecto</h5>
                        <button className="btn btn-primary" onClick={handleCreateClick} disabled={updating || creating}>
                            <i className="fa fa-plus-circle me-2" aria-hidden="true" />
                            Agregar Detalle de Proyecto
                        </button>
                    </div>
                </div>
                <hr className="mx-0" style={{ border: '2px', margin: '5px' }} />
            </div>
            {successMessage && <Alert variant="success">{successMessage}</Alert>}
            <DataTableWrapper
                data={data || []}
                columns={columns}
                onEdit={handleEditClick}
                onDelete={openDeleteFloorPlan}
                paginator={false}
                totalRecords={data?.length || 0}
                emptyMessage="No se encontraron registros."
                sortField="name"
                sortOrder={1}
                lazy={false}
                showGridlines={true}
            />
            <EditProjectFloorPlanModal
                show={isOpenEditFloorPlan}
                floorPlan={selectedFloorPlan}
                handleClose={closedEditFloorPlan}
                handleSave={handleModalSave}
                updating={updating || creating || creatingFloorDetail || uploadingFloorDetail || deletingFloorDetail}
                errors={errors}
                requestError={creatingError || updatingError}
                projectsOptions={projectsOptions}
                features={memoizedFloorPlans}
                deleteFeature={deleteFloorDetail}
            />
            <ConfirmDeleteModal
                show={isOpenDeleteFloorPlan}
                onHide={closedDeleteFloorPlan}
                onConfirm={handleDeleteConfirm}
                deleting={deleting}
                error={deletingError}
            />
        </div>
    );
};

export default React.memo(ProjectsFloorPlan);